import React, { useEffect, useRef, useState, useMemo, createContext, useContext } from "react";
import { IconArrowNarrowLeft, IconArrowNarrowRight } from "@tabler/icons-react";
import { Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { getS3DownloadUrl } from "@/services/data/s3Files";
import { useSignedUrl } from "@/hooks/useSignedUrl";

interface CarouselProps {
  items: React.ReactElement[];
  initialScroll?: number;
}

type Card = {
  src?: string;
  title: string;
  category: string;
  videoUrl?: string;
};

export const CarouselContext = createContext<{
  onCardClose: (index: number) => void;
  currentIndex: number;
  onOpenVideo?: (videoUrl: string) => void;
}>({
  onCardClose: () => {},
  currentIndex: 0,
  onOpenVideo: undefined
});

interface CarouselComponentProps extends CarouselProps {
  onOpenVideo?: (videoUrl: string) => void;
}

export const Carousel = ({
  items,
  initialScroll = 0,
  onOpenVideo
}: CarouselComponentProps) => {
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = initialScroll;
      checkScrollability();
    }
  }, [initialScroll]);

  const checkScrollability = () => {
    if (carouselRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: -300,
        behavior: "smooth"
      });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: 300,
        behavior: "smooth"
      });
    }
  };

  const handleCardClose = (index: number) => {
    if (carouselRef.current) {
      const cardWidth = isMobile() ? 232 : 461;
      const gap = isMobile() ? 4 : 8;
      const scrollPosition = (cardWidth + gap) * (index + 1);
      carouselRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth"
      });
      setCurrentIndex(index);
    }
  };

  const isMobile = () => {
    return window && window.innerWidth < 768;
  };

  const contextValue = useMemo(() => ({
    onCardClose: handleCardClose,
    currentIndex,
    onOpenVideo
  }), [currentIndex, onOpenVideo]);

  return (
    <CarouselContext.Provider value={contextValue}>
      <div className="relative w-full">
        <div 
          className="flex w-full overflow-x-scroll overscroll-x-auto py-2 [scrollbar-width:none] md:py-20 snap-x snap-mandatory"
          style={{
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch'
          }}
          ref={carouselRef} 
          onScroll={checkScrollability}
        >
          <div className={cn("absolute right-0 z-[1000] h-auto w-[5%] overflow-hidden bg-gradient-to-l hidden md:block")}></div>

          <div className={cn("flex flex-row justify-start gap-4 pl-4", "md:mx-auto md:max-w-7xl")}>
            {items.map((item, index) => (
              <div 
                key={"card" + index} 
                className="rounded-3xl last:pr-4 md:last:pr-[33%] last:rounded-r-3xl snap-center"
                style={{
                  opacity: 1,
                  transform: 'translateZ(0)',
                  willChange: 'scroll-position'
                }}
              >
                {item}
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pr-4 md:pr-10">
          <button className="relative z-40 flex h-10 w-10 items-center justify-center rounded-full bg-muted disabled:opacity-50" onClick={scrollLeft} disabled={!canScrollLeft}>
            <IconArrowNarrowLeft className="h-6 w-6 text-muted-foreground" />
          </button>
          <button className="relative z-40 flex h-10 w-10 items-center justify-center rounded-full bg-muted disabled:opacity-50" onClick={scrollRight} disabled={!canScrollRight}>
            <IconArrowNarrowRight className="h-6 w-6 text-muted-foreground" />
          </button>
        </div>
      </div>
    </CarouselContext.Provider>
  );
};

export const Card = ({
  card,
  index,
  layout = false
}: {
  card: Card;
  index: number;
  layout?: boolean;
}) => {
  const { onOpenVideo } = useContext(CarouselContext);
  const resolvedSrc = useSignedUrl(card.src);
  const [resolvedVideoUrl, setResolvedVideoUrl] = useState<string | null>(null);

  // Resolve S3 key to signed URL for video thumbnail fallback
  useEffect(() => {
    if (card.src) return; // Has thumbnail, no need
    if (!card.videoUrl) return;
    if (card.videoUrl.startsWith('http') || card.videoUrl.startsWith('blob:')) {
      setResolvedVideoUrl(card.videoUrl);
      return;
    }
    getS3DownloadUrl(card.videoUrl, false).then(result => {
      if (result.success && result.data?.url) {
        setResolvedVideoUrl(result.data.url);
      }
    });
  }, [card.videoUrl, card.src]);

  const handleOpen = () => {
    if (card.videoUrl && onOpenVideo) {
      onOpenVideo(card.videoUrl);
    }
  };

  const renderBackgroundMedia = () => {
    if (resolvedSrc) {
      return <BlurImage src={resolvedSrc} alt={card.title} className="absolute inset-0 z-10 object-cover" />;
    }
    if (resolvedVideoUrl) {
      return (
        <video
          src={`${resolvedVideoUrl}#t=0.5`}
          className="absolute inset-0 z-10 w-full h-full object-cover"
          muted
          playsInline
          preload="metadata"
        />
      );
    }
    return null;
  };

  return (
    <button
      onClick={handleOpen}
      className="relative z-10 flex h-96 w-[216px] flex-col items-start justify-start overflow-hidden rounded-2xl bg-muted md:h-[384px] md:w-[230px] transition-transform hover:scale-[1.02] active:scale-[0.98]"
      style={{ transform: 'translateZ(0)', willChange: 'transform' }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 z-30 h-full bg-gradient-to-b from-black/50 via-transparent to-transparent" />
      <div className="relative z-40 p-8">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
          <Play className="h-5 w-5 text-white fill-white" />
        </div>
        <p className="mt-2 max-w-xs text-left font-sans text-xl font-semibold [text-wrap:balance] text-white md:text-3xl">
          {card.title}
        </p>
      </div>
      {renderBackgroundMedia()}
    </button>
  );
};

export const BlurImage = ({
  src,
  className,
  alt,
  ...rest
}: React.ImgHTMLAttributes<HTMLImageElement>) => {
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);

  // Check if image is already cached/loaded on mount
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalHeight > 0) {
      setIsLoading(false);
    }
  }, []);

  return (
    <img 
      ref={imgRef}
      className={cn("h-full w-full transition duration-300", isLoading ? "blur-sm" : "blur-0", className)} 
      onLoad={() => setIsLoading(false)} 
      src={src} 
      loading="lazy" 
      decoding="async" 
      alt={alt || "Background of a beautiful view"} 
      {...rest} 
    />
  );
};